// /link — Ring App Store account-link form (pre-session, public route).
//
// Ring's portal redirects the browser here with ?nonce=&time=. This page runs
// BEFORE any session exists (the install ceremony mints the session later, in
// the OIDC callback), so it is in the SessionProvider pre-session allowlist and
// uses skipActingRole calls. Replaces the former server-rendered
// GET/POST /ringappstore/link HTML — the backend now serves only typed JSON
// (/api/ring-link/validate + /submit) and the cert strings live in
// ringLinkMessages.
//
// Flow: validate the nonce/time params (show an error page if stale/invalid) →
// render the workspace-name form → POST submit → top-level navigate to the
// Federate authorize redirect the backend returns.

import { useEffect, useState } from "react";
import { ringLinkClient } from "@/ringlink/ringLinkClient";
import { ringLinkMessage } from "@/ringlink/ringLinkMessages";

const MIN_NAME = 3;
const MAX_NAME = 80;

type State =
  | { kind: "validating" }
  | { kind: "ready" }
  | { kind: "error"; message: string }
  | { kind: "submitting" };

function readParams(): { nonce: string; time: string } {
  const q = new URLSearchParams(window.location.search);
  return { nonce: q.get("nonce") ?? "", time: q.get("time") ?? "" };
}

export function RingLinkPage(): JSX.Element {
  const [{ nonce, time }] = useState(readParams);
  const [state, setState] = useState<State>({ kind: "validating" });
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const res = await ringLinkClient.validate(nonce, time, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (res.valid) {
          setState({ kind: "ready" });
        } else {
          setState({ kind: "error", message: ringLinkMessage(res.error_code) });
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setState({ kind: "error", message: ringLinkMessage("internal") });
      }
    })();
    return () => ctrl.abort();
  }, [nonce, time]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < MIN_NAME || trimmed.length > MAX_NAME) {
      setNameError(ringLinkMessage("name_invalid"));
      return;
    }
    setNameError(null);
    setState({ kind: "submitting" });
    try {
      const res = await ringLinkClient.submit(trimmed, nonce, time);
      if (res.valid && res.redirect_to) {
        // Top-level navigation: the next leg is a backend 302 to Federate
        // (cookie jar + cross-origin), which fetch() cannot follow.
        window.location.assign(res.redirect_to);
        return;
      }
      if (res.error_code === "name_invalid") {
        setNameError(ringLinkMessage("name_invalid"));
        setState({ kind: "ready" });
        return;
      }
      setState({ kind: "error", message: ringLinkMessage(res.error_code) });
    } catch (err) {
      setState({ kind: "error", message: ringLinkMessage("internal") });
    }
  }

  if (state.kind === "validating") {
    return (
      <div className="reb-page">
        <div className="reb-card" style={{ textAlign: "center" }}>
          <h1 className="reb-page__title">Setting up…</h1>
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="reb-page">
        <div className="reb-card" style={{ textAlign: "center" }}>
          <h1 className="reb-page__title">We can't finish linking right now</h1>
          <p className="reb-error" role="alert">
            {state.message}
          </p>
          <p className="reb-page__subtitle">
            Please reinstall the bridge from the Ring app and try again.
          </p>
        </div>
      </div>
    );
  }

  const submitting = state.kind === "submitting";
  return (
    <div className="reb-page">
      <div className="reb-card">
        <h1 className="reb-page__title">Set up your enterprise workspace</h1>
        <p className="reb-page__subtitle">
          Choose a name for your workspace, then sign in with Amazon to finish
          linking.
        </p>
        <form onSubmit={handleSubmit}>
          <label htmlFor="tenant_name" style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>
            Workspace name
          </label>
          <input
            id="tenant_name"
            name="tenant_name"
            type="text"
            required
            minLength={MIN_NAME}
            maxLength={MAX_NAME}
            autoComplete="organization"
            placeholder="e.g., IAD12 Security Team"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: 12,
              border: "1px solid #ccc",
              borderRadius: 8,
              fontSize: 16,
            }}
          />
          <p className="reb-page__subtitle" style={{ fontSize: 12 }}>
            3–80 characters. You can change this later.
          </p>
          {nameError && (
            <p className="reb-error" role="alert">
              {nameError}
            </p>
          )}
          <button type="submit" className="reb-button" disabled={submitting}>
            {submitting ? "Redirecting…" : "Sign in with Amazon →"}
          </button>
        </form>
      </div>
    </div>
  );
}
